import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../lib/auth';
import { session } from '../lib/api';
import { useTickets } from '../lib/ticket-state';
import type { Ticket } from '../types';

export function RealtimeBridge() {
  const { user, can } = useAuth();
  const { setSnapshot, upsert, remove, removeFromQueue, setRealtimeStatus } = useTickets();

  useEffect(() => {
    const token = session.token();
    if (!user || !token || !can('tichete')) {
      setRealtimeStatus('disconnected');
      return;
    }

    setRealtimeStatus('connecting');
    const socket = io({
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
    });
    const changed = () => window.dispatchEvent(new Event('nova:ticket-change'));
    socket.on('connect', () => setRealtimeStatus('connected'));
    socket.on('disconnect', () => setRealtimeStatus('disconnected'));
    socket.on('connect_error', () => setRealtimeStatus('error'));
    // REST pages already load on mount. Initial snapshot completion is not a
    // ticket mutation and must not trigger a second full dashboard refresh.
    socket.on('ticket:snapshot', (items: Ticket[]) => { setSnapshot(Array.isArray(items) ? items : []); });
    socket.on('ticket:snapshot-error', () => { setRealtimeStatus('error'); });
    socket.on('ticket:added', (ticket: Ticket, meta?: { initial?: boolean }) => { upsert(ticket); if (!meta?.initial) changed(); });
    socket.on('ticket:changed', (ticket: Ticket) => { upsert(ticket); changed(); });
    socket.on('ticket:removed', (payload: { id?: string }) => { if (payload?.id) remove(payload.id); changed(); });
    socket.on('ticket:queue-removed', (payload: { id?: string }, meta?: { initial?: boolean }) => {
      if (payload?.id) removeFromQueue(payload.id);
      // Lists have their own progressive state; remove the obsolete active card
      // immediately while leaving completed run/report rows intact.
      if (payload?.id) window.dispatchEvent(new CustomEvent('nova:ticket-queue-removed', { detail: { id: payload.id } }));
      if (!meta?.initial) changed();
    });
    socket.on('stock:changed', () => window.dispatchEvent(new Event('nova:stock-change')));
    socket.on('stock-return:changed', () => window.dispatchEvent(new Event('nova:stock-return-change')));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      setRealtimeStatus('disconnected');
    };
  }, [user, can, setSnapshot, upsert, remove, removeFromQueue, setRealtimeStatus]);

  return null;
}
